// Industry-specific copy for the leads-first CTA on IndustryMarketingPage.
// Keyed by industry slug; falls back to a generic variant if missing.

export type IndustryLeadsCopy = {
  /** Short eyebrow above headline, e.g. "Ready for more emergency HVAC calls?" */
  eyebrow: string;
  /** Type of call phrased for this trade, e.g. "emergency repair calls" */
  callType: string;
  /** Type of booked job for this trade, e.g. "installs and tune-ups" */
  jobType: string;
  /** 3 outcome bullets — concrete, trade-specific, leads/calls/jobs framed. */
  bullets: [string, string, string];
  /** CTA paragraph under the headline. */
  subhead: string;
};

const DEFAULT: IndustryLeadsCopy = {
  eyebrow: "Ready to grow?",
  callType: "qualified phone calls",
  jobType: "booked jobs",
  bullets: [
    "More qualified inbound leads from Google",
    "More phone calls from buyers ready to hire",
    "More booked jobs on the calendar each week",
  ],
  subhead:
    "Pick your Colorado market below — every page is a playbook built to fill your schedule.",
};

const MAP: Record<string, IndustryLeadsCopy> = {
  "hvac-marketing-colorado": {
    eyebrow: "Ready for more HVAC calls?",
    callType: "emergency repair and no-heat calls",
    jobType: "installs, tune-ups, and service calls",
    bullets: [
      "More no-heat / no-AC emergency calls when the weather turns",
      "More qualified install leads (heat pumps, full systems, mini-splits)",
      "More booked maintenance plans that fill the slow season",
    ],
    subhead:
      "Pick your Colorado market — every page is built to drive emergency calls, install leads, and recurring maintenance plans for HVAC.",
  },
  "plumbing-marketing-colorado": {
    eyebrow: "Ready for more plumbing calls?",
    callType: "emergency plumbing calls",
    jobType: "repairs, re-pipes, and water-heater installs",
    bullets: [
      "More emergency leak, drain, and water-heater calls",
      "More high-ticket re-pipe and sewer-line leads",
      "More booked service calls every week, not just emergencies",
    ],
    subhead:
      "Pick your Colorado market — every page is built to drive emergency calls and high-ticket repipe and water-heater jobs.",
  },
  "roofing-marketing-colorado": {
    eyebrow: "Ready for more roofing leads?",
    callType: "hail, storm, and inspection calls",
    jobType: "full roof replacements and repairs",
    bullets: [
      "More storm-response and hail-damage inspection calls",
      "More insurance-paid full replacement leads",
      "More booked jobs before out-of-state storm chasers arrive",
    ],
    subhead:
      "Pick your Colorado market — every page is tuned to capture hail and storm-season demand before the chasers do.",
  },
  "electrical-marketing-colorado": {
    eyebrow: "Ready for more electrical calls?",
    callType: "service and panel-upgrade calls",
    jobType: "panel upgrades, EV chargers, and rewires",
    bullets: [
      "More panel-upgrade and EV-charger install leads",
      "More residential service calls (outlets, breakers, lighting)",
      "More commercial bid invitations and repeat accounts",
    ],
    subhead:
      "Pick your Colorado market — every page is built to drive panel-upgrade, EV-charger, and high-ticket residential calls.",
  },
  "landscaping-marketing-colorado": {
    eyebrow: "Ready for more landscaping jobs?",
    callType: "design-build and maintenance calls",
    jobType: "design-build projects and seasonal contracts",
    bullets: [
      "More high-ticket design-build and hardscape leads",
      "More recurring weekly/biweekly maintenance contracts",
      "More booked snow-removal accounts before winter",
    ],
    subhead:
      "Pick your Colorado market — every page is built to fill design-build, maintenance, and snow-removal calendars.",
  },
  "contractor-marketing-colorado": {
    eyebrow: "Ready for more remodel leads?",
    callType: "remodel and addition calls",
    jobType: "kitchens, baths, additions, and whole-home remodels",
    bullets: [
      "More qualified kitchen and bath remodel leads",
      "More addition and ADU project consultations",
      "More booked design-build jobs from buyers ready to spend",
    ],
    subhead:
      "Pick your Colorado market — every page is built to fill the calendar with qualified remodel and addition leads.",
  },
  "dental-marketing-colorado": {
    eyebrow: "Ready for more new patients?",
    callType: "new-patient calls",
    jobType: "new patient and high-value case bookings",
    bullets: [
      "More new-patient calls from Google and Maps",
      "More implant, Invisalign, and cosmetic case inquiries",
      "More booked exams from insurance-friendly searches",
    ],
    subhead:
      "Pick your Colorado market — every page is built to drive new-patient calls and high-value case bookings.",
  },
  "law-firm-marketing-colorado": {
    eyebrow: "Ready for more case calls?",
    callType: "qualified case-intake calls",
    jobType: "signed cases and retainer agreements",
    bullets: [
      "More qualified case calls (filtered, not tire-kickers)",
      "More signed retainers from practice-area landing pages",
      "More referrals from a search presence that builds authority",
    ],
    subhead:
      "Pick your Colorado market — every page is built to drive practice-area-specific intake calls and signed cases.",
  },
  "dog-trainer-marketing-colorado": {
    eyebrow: "Ready to fill your board-and-train?",
    callType: "board-and-train and behavior-mod calls",
    jobType: "board-and-train slots and behavior programs",
    bullets: [
      "More board-and-train inquiries from buyers ready to pay",
      "More reactive-dog and behavior-mod consultations",
      "More booked puppy-class seats during spring season",
    ],
    subhead:
      "Pick your Colorado market — every page is built to fill board-and-train slots and behavior-mod programs.",
  },
  "pest-control-marketing-colorado": {
    eyebrow: "Ready for more pest calls?",
    callType: "urgent pest and exterminator calls",
    jobType: "one-time treatments and quarterly plans",
    bullets: [
      "More urgent mice, wasp, and bed-bug calls in season",
      "More signed quarterly residential plans",
      "More commercial restaurant and multifamily accounts",
    ],
    subhead:
      "Pick your Colorado market — every page is built to capture seasonal surges and build recurring quarterly routes.",
  },
  "tree-service-marketing-colorado": {
    eyebrow: "Ready for more tree jobs?",
    callType: "removal and storm-response calls",
    jobType: "removals, trimming, and stump grinding",
    bullets: [
      "More storm-response calls when the wind hits",
      "More scheduled trimming and removal bookings",
      "More HOA and commercial maintenance contracts",
    ],
    subhead:
      "Pick your Colorado market — every page is built to capture storm response and fill the trimming and removal calendar.",
  },
};

export function getIndustryLeadsCopy(slug: string, industry: string, industryLower: string): IndustryLeadsCopy {
  const found = MAP[slug];
  if (found) return found;
  // Fallback — interpolate the industry name into the default copy.
  return {
    ...DEFAULT,
    eyebrow: `Ready for more ${industryLower} leads?`,
    bullets: [
      `More qualified ${industryLower} leads from Google search`,
      `More phone calls from ${industryLower} buyers ready to hire`,
      `More booked ${industry.toLowerCase()} jobs on the calendar each week`,
    ],
    subhead: `Pick your Colorado market below — every page is a playbook built to fill your ${industryLower} schedule.`,
  };
}
